import React, { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineShare } from "react-icons/hi";
import { Spinner } from "../../atoms/Spinner";
import { CustomButton } from "../../atoms/CustomButton";
import { ModalShell } from "../../atoms/ModalShell";
import QRCodeGenerator from "../../atoms/QRCodeGenerator";
import LinkActions from "../LinkActions";

interface ShareEventModalProps {
  show: boolean;
  onClose: () => void;
  eventName: string;
  shareUrl: string;
}

const ShareEventModal: React.FC<ShareEventModalProps> = ({
  show,
  onClose,
  eventName,
  shareUrl,
}) => {
  const { t } = useTranslation();
  const qrGeneratorRef = useRef<any>(null);

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <HiOutlineShare className="text-blue-600 flex-shrink-0" size={18} />
          <span className="truncate">
            {eventName || t("components.eventPayments.shareModal.title")}
          </span>
        </span>
      }
      footer={
        <CustomButton
          variant="secondary"
          onClick={onClose}
          title={t("common.close")}
          className="px-6"
        />
      }
    >
      {shareUrl ? (
        <div className="flex flex-col w-full gap-3 sm:gap-4 text-center">
          <div className="grid justify-items-center gap-2">
            <QRCodeGenerator
              ref={qrGeneratorRef}
              data={shareUrl}
              size={250}
              showLogo={true}
              logoUrl="https://cdn.1cdn.app/application/UMD/2405110558455_logo.png"
              ecLevel="H"
              className="mb-2"
              ariaLabel="QR code for event ticket purchase"
            />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
              {t("components.eventPayments.shareModal.scanToPurchase")}
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm max-w-xs sm:max-w-sm px-2 leading-tight">
              {t("components.eventPayments.shareModal.shareDescription")}
            </p>
          </div>

          <div className="relative mx-4">
            <div className="absolute inset-0 grid items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative grid justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">
                {t("components.eventPayments.shareModal.or")}
              </span>
            </div>
          </div>

          <LinkActions
            url={shareUrl}
            qrGeneratorRef={qrGeneratorRef}
            downloadFileName={`event_${eventName}_qr.png`}
          />
        </div>
      ) : (
        <div className="text-center py-8">
          <Spinner size="xl" />
          <p className="text-sm text-gray-600 mt-2">
            {t("components.eventPayments.shareModal.generatingLink")}
          </p>
        </div>
      )}
    </ModalShell>
  );
};

export default memo(ShareEventModal);
